import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useCart } from "../../context/CartContext";
import "../../styles/style_boutique.css";

// Icônes de taille avec émojis floraux
const SIZE_ICONS = {
  M: { icon: "🌱", label: "M", description: "Moyen" },
  L: { icon: "🌿", label: "L", description: "Grand" },
  XL: { icon: "🌳", label: "XL", description: "Très Grand" }
};

export default function QuickViewModal({ product, isOpen, onClose }) {
  const { addToCart } = useCart();
  const [selectedVariant, setSelectedVariant] = useState(
    product?.variantes?.[0] || null
  );
  const [selectedBouquetType, setSelectedBouquetType] = useState(product?.bouquetTypeDefault || null);
  const [quantity, setQuantity] = useState(1);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Réinitialiser quand le produit change
  useEffect(() => {
    if (product) {
      setSelectedVariant(product.variantes?.[0] || null);
      setSelectedBouquetType(product.bouquetTypeDefault || null);
      setQuantity(1);
      setCurrentImageIndex(0);
    }
  }, [product]);

  // Fermer avec Escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") onClose();
    };
    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose]);

  if (!isOpen || !product) return null;

  const hasMultipleVariants = product.variantes && product.variantes.length > 1;

  const calculateTTC = (prixHT, tva) => {
    return (prixHT * (1 + tva / 100)).toFixed(2);
  };

  const currentPrice = selectedVariant
    ? calculateTTC(selectedVariant.prixHT, product.tva)
    : "0.00";

  const handleAddToCart = () => {
    if (selectedVariant) {
      addToCart(product, selectedVariant, quantity, selectedBouquetType);
    }
    onClose();
  };

  const flowerEmojis = ["🌸", "🌺", "🌷", "🌹", "💐", "🌻", "🪻", "🪷"];
  const randomFlower = flowerEmojis[product.id % flowerEmojis.length];

  const placeholderStyle = {
    background: `linear-gradient(135deg, 
      hsl(${(product.id * 30) % 360}, 70%, 85%), 
      hsl(${(product.id * 30 + 40) % 360}, 60%, 75%))`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '6rem'
  };

  // Utiliser un Portal pour rendre la modal en dehors de la hiérarchie du composant
  return createPortal(
    <div className="quick-view-overlay" onClick={onClose}>
      <div className="quick-view-modal" onClick={(e) => e.stopPropagation()}>
        {/* Bouton fermer */}
        <button className="quick-view-close" onClick={onClose}>
          <ion-icon name="close-outline"></ion-icon>
        </button>

        <div className="quick-view-content">
          {/* Image section */}
          <div className="quick-view-image-section">
            <div className="quick-view-main-image">
              {product.imgPrincipal ? (
                <img src={product.imgPrincipal} alt={product.libelle} />
              ) : (
                <div className="quick-view-placeholder" style={placeholderStyle}>
                  {randomFlower}
                </div>
              )}
            </div>
            <span className="quick-view-category">{product.categorie}</span>
          </div>

          {/* Info section */}
          <div className="quick-view-info-section">
            <h2 className="quick-view-title">{product.libelle}</h2>
            
            <p className="quick-view-description">{product.description}</p>

            {/* Stock info */}
            {selectedVariant && (
              <div className={`quick-view-stock ${selectedVariant.stock <= 3 ? 'low' : ''} ${selectedVariant.stock === 0 ? 'out' : ''}`}>
                {selectedVariant.stock === 0 ? (
                  <>
                    <ion-icon name="close-circle-outline"></ion-icon>
                    <span>Rupture de stock</span>
                  </>
                ) : selectedVariant.stock <= 3 ? (
                  <>
                    <ion-icon name="alert-circle-outline"></ion-icon>
                    <span>Plus que {selectedVariant.stock} en stock</span>
                  </>
                ) : (
                  <>
                    <ion-icon name="checkmark-circle-outline"></ion-icon>
                    <span>En stock ({selectedVariant.stock} disponibles)</span>
                  </>
                )}
              </div>
            )}

            {/* Sélecteur de taille */}
            {hasMultipleVariants && (
              <div className="quick-view-sizes">
                <span className="quick-view-label">Choisir la taille :</span>
                <div className="quick-view-size-options">
                  {product.variantes.map((variant) => {
                    const sizeInfo = SIZE_ICONS[variant.taille_code] || {
                      icon: "📦",
                      label: variant.taille_code,
                      description: variant.taille_libelle
                    };
                    const isSelected = selectedVariant?.taille_code === variant.taille_code;
                    const isOutOfStock = variant.stock === 0;

                    return (
                      <button
                        key={variant.taille_code}
                        className={`quick-view-size-btn ${isSelected ? 'selected' : ''} ${isOutOfStock ? 'disabled' : ''}`}
                        onClick={() => !isOutOfStock && setSelectedVariant(variant)}
                        disabled={isOutOfStock}
                      >
                        <span className="size-icon">{sizeInfo.icon}</span>
                        <span className="size-code">{sizeInfo.label}</span>
                        <span className="size-desc">{variant.taille_libelle}</span>
                        <span className="size-price">{calculateTTC(variant.prixHT, product.tva)} €</span>
                        {isOutOfStock && <span className="size-out-badge">Épuisé</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Prix */}
            <div className="quick-view-price-section">
              <span className="quick-view-price-label">Prix TTC</span>
              <span className="quick-view-price">{currentPrice} €</span>
            </div>

            {/* Actions */}
            <div className="quick-view-actions">
              <div className="quick-view-quantity">
                <button
                  className="qty-btn"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  disabled={quantity <= 1}
                >
                  <ion-icon name="remove-outline"></ion-icon>
                </button>
                <span className="qty-value">{quantity}</span>
                <button
                  className="qty-btn"
                  onClick={() => setQuantity(Math.min(selectedVariant?.stock || 10, quantity + 1))}
                  disabled={quantity >= (selectedVariant?.stock || 10)}
                >
                  <ion-icon name="add-outline"></ion-icon>
                </button>
              </div>

              <button
                className="quick-view-add-btn"
                onClick={handleAddToCart}
                disabled={!selectedVariant || selectedVariant.stock === 0}
              >
                <ion-icon name="cart-outline"></ion-icon>
                <span>Ajouter au panier</span>
              </button>
            </div>

            {/* Infos supplémentaires */}
            <div className="quick-view-extras">
              <div className="extra-item">
                <ion-icon name="leaf-outline"></ion-icon>
                <span>Fleurs fraîches du jour</span>
              </div>
              <div className="extra-item">
                <ion-icon name="gift-outline"></ion-icon>
                <span>Emballage soigné offert</span>
              </div>
              <div className="extra-item">
                <ion-icon name="bicycle-outline"></ion-icon>
                <span>Livraison express disponible</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
